import moment from 'moment-timezone';
import 'moment/locale/ja';

export const TOKYO_TZ = 'Asia/Tokyo';

/**
 * Format a reservation datetime for display.
 * Used for ビデオ診療 予定日 — renders the API value as-is without timezone conversion.
 */
export function formatReservationInTokyo(
  value: string | null | undefined,
  format: string,
): string {
  if (!value) {
    return '';
  }

  return moment(value).locale('ja').format(format);
}

/**
 * Split API reservation_date into date/time form fields (JST).
 */
export function reservationDateToFormParts(
  reservationDate: string,
  formatDate: string,
  formatTime: string,
): { date: string; time: string } {
  const m = moment.tz(reservationDate, TOKYO_TZ);

  return {
    date: m.format(formatDate),
    time: m.format(formatTime),
  };
}

/**
 * Build reservation_date payload from modal date + time (interpreted as JST).
 */
export function buildReservationDatePayload(date: string, time: string): string {
  return moment.tz(`${date} ${time}:00`, TOKYO_TZ).format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Add minutes to reservation_date (for calendar end time, video slot length).
 * Renders the API value as-is without timezone conversion.
 */
export function addReservationMinutesInTokyo(
  value: string | null | undefined,
  minutes: number,
  format: string,
): string {
  if (!value) {
    return '';
  }

  return moment(value).locale('ja').add(minutes, 'minutes').format(format);
}

/**
 * Format a stored timestamp — or "now", when there is none yet — in Japan time,
 * whatever timezone the browser is in.
 *
 * Unlike formatReservationInTokyo, this CONVERTS, and handles both shapes the
 * API sends for the same kind of value:
 *  - ISO-8601 with an offset (`created_at`, `updated_at`: `…+09:00`, `…Z`) is
 *    converted to Tokyo;
 *  - a naive `Y-m-d H:i:s` string (`created_at_jp`) is read AS Tokyo time.
 * Plain `moment(value)` gets the first wrong outside Japan — it renders the
 * browser's local time — and `moment()` does the same for "now". A doctor in
 * UTC+7 saw a new karte stamped two hours earlier than its own 一時保存 label
 * (No.173).
 *
 * Returns '' for an unparseable value rather than "Invalid date".
 */
export function formatInTokyo(value: string | null | undefined, format: string): string {
  const m = value ? moment.tz(value, TOKYO_TZ) : moment.tz(TOKYO_TZ);
  return m.isValid() ? m.format(format) : '';
}
